import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Scale, Award, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { ModuleShell, DataTable, StatusBadge, Kpi, Field, ExportButtons } from "@/components/partners/common";
import { Sel } from "@/components/partners/PartnerForm";
import { fmtMoney, fmtNum, fmtDate, titleCase, apiError } from "@/lib/partnerApi";

export function SupplierRfqs() {
  const [rows, setRows] = useState([]);
  const [sups, setSups] = useState([]);
  const [items, setItems] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => api.get("/supplier-master/rfqs").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => {
    load();
    api.get("/supplier-master", { params: { status: "active" } }).then((r) => setSups(r.data)).catch(() => {});
    api.get("/items").then((r) => setItems(r.data)).catch(() => {});
  }, [load]);
  return (
    <ModuleShell kind="supplier" title="RFQ & Supplier Quotations" tid="rfq-page"
      subtitle="Supplier → RFQ → Quotation → compare → award → Purchase Request → PO → GRN → QC → Invoice → Payment."
      actions={<Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setNewOpen(true)} data-testid="new-rfq-btn"><Plus className="w-4 h-4 mr-1" /> New RFQ</Button>}>
      <DataTable rows={rows} tid="rfq-table" empty="No RFQs yet." onRowClick={(r) => setSel(r.id)} columns={[
        { key: "code", label: "RFQ" }, { key: "title", label: "Title" }, { key: "created_at", label: "Date", type: "date" },
        { key: "due_date", label: "Quotes Due", type: "date" }, { key: "lines", label: "Items", render: (r) => r.lines.map((l) => l.item_code).join(", ") },
        { key: "supplier_ids", label: "Suppliers", render: (r) => r.supplier_ids.length }, { key: "quotation_count", label: "Quotes", type: "number" },
        { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status === "sent" ? "open" : r.status === "awarded" ? "approved" : r.status} /> },
        { key: "purchase_request_code", label: "PR" },
      ]} />
      <NewRfq open={newOpen} onOpenChange={setNewOpen} sups={sups} items={items} onDone={load} />
      {sel && <RfqDetail id={sel} onClose={() => { setSel(null); load(); }} />}
    </ModuleShell>
  );
}

function NewRfq({ open, onOpenChange, sups, items, onDone }) {
  const [f, setF] = useState({ title: "", due_date: "", supplier_ids: [], lines: [{ item_id: "", qty: "" }], email_suppliers: false });
  useEffect(() => { if (open) setF({ title: "", due_date: "", supplier_ids: [], lines: [{ item_id: "", qty: "" }], email_suppliers: false }); }, [open]);
  const [suggest, setSuggest] = useState([]);
  useEffect(() => {
    const iid = f.lines[0]?.item_id;
    if (!iid) { setSuggest([]); return; }
    api.get(`/supplier-master/item-suppliers/${iid}`).then((r) => setSuggest(r.data)).catch(() => setSuggest([]));
  }, [f.lines]);
  const save = async () => {
    try {
      await api.post("/supplier-master/rfqs", { ...f, lines: f.lines.filter((l) => l.item_id).map((l) => ({ ...l, qty: Number(l.qty) })) });
      toast.success("RFQ created"); onOpenChange(false); onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = (id) => setF({ ...f, supplier_ids: f.supplier_ids.includes(id) ? f.supplier_ids.filter((x) => x !== id) : [...f.supplier_ids, id] });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Request for Quotation</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
          <Field label="Quotes Due By"><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></Field>
        </div>
        <div className="space-y-2">
          <div className="text-sm font-semibold text-slate-700">Items</div>
          {f.lines.map((l, i) => (
            <div key={i} className="flex gap-2 items-center">
              <div className="flex-1"><Sel value={l.item_id} onChange={(v) => setF({ ...f, lines: f.lines.map((x, j) => (j === i ? { ...x, item_id: v } : x)) })}
                options={items.map((it) => ({ value: it.id, label: `${it.code} · ${it.name}` }))} placeholder="Choose item" tid={`rfq-item-${i}`} /></div>
              <Input className="w-32" type="number" placeholder="Qty" value={l.qty} onChange={(e) => setF({ ...f, lines: f.lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)) })} data-testid={`rfq-qty-${i}`} />
              <Button variant="ghost" size="icon" onClick={() => setF({ ...f, lines: f.lines.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setF({ ...f, lines: [...f.lines, { item_id: "", qty: "" }] })}><Plus className="w-4 h-4 mr-1" /> Item</Button>
        </div>
        <div className="space-y-2">
          <div className="text-sm font-semibold text-slate-700">Invite Suppliers {suggest.length > 0 && <span className="text-xs font-normal text-slate-500">— mapped to the first item: {suggest.map((s) => `${s.supplier_name} (${s.role})`).join(", ")}</span>}</div>
          <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto border border-slate-200 rounded-md p-2">
            {sups.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.supplier_ids.includes(s.id)} onChange={() => toggle(s.id)} />{s.code} · {s.name}</label>
            ))}
            {sups.length === 0 && <div className="text-xs text-slate-500">No active suppliers.</div>}
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={f.email_suppliers} onChange={(e) => setF({ ...f, email_suppliers: e.target.checked })} /> Email the RFQ to each supplier's primary contact</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="save-rfq-btn">Create RFQ</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RfqDetail({ id, onClose }) {
  const [r, setR] = useState(null);
  const [cmp, setCmp] = useState(null);
  const [q, setQ] = useState(null);
  const [awards, setAwards] = useState({});
  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get(`/supplier-master/rfqs/${id}`), api.get(`/supplier-master/rfqs/${id}/compare`)]);
      setR(a.data); setCmp(b.data);
      const rec = {};
      b.data.items.forEach((it) => { const o = it.offers.find((x) => x.recommended); if (o) rec[it.item_id] = o.quotation_id; });
      setAwards(rec);
    } catch (e) { toast.error(apiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const addQuote = async () => {
    try {
      await api.post(`/supplier-master/rfqs/${id}/quotations`, { ...q, lines: q.lines.filter((l) => l.unit_price).map((l) => ({ ...l, unit_price: Number(l.unit_price), lead_time_days: Number(l.lead_time_days || 0), tax_pct: Number(l.tax_pct || 0), moq: Number(l.moq || 0) })) });
      toast.success("Quotation recorded"); setQ(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const award = async () => {
    try {
      const { data } = await api.post(`/supplier-master/rfqs/${id}/award`, { awards: Object.entries(awards).map(([item_id, quotation_id]) => ({ item_id, quotation_id })), update_mapping: true, create_purchase_request: true });
      toast.success(`Awarded${data.purchase_request ? ` — ${data.purchase_request.code} created` : ""}`); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="rfq-detail">
        <DialogHeader><DialogTitle>{r ? `${r.code} ${r.title ? `· ${r.title}` : ""}` : "RFQ"}</DialogTitle></DialogHeader>
        {!r || !cmp ? <div className="text-sm text-slate-500">Loading…</div> : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-sm flex-wrap">
              <StatusBadge value={r.status === "sent" ? "open" : r.status === "awarded" ? "approved" : r.status} />
              <span>Due {fmtDate(r.due_date)}</span>
              <span className="text-slate-500">Invited: {r.suppliers.map((s) => s.name).join(", ")}</span>
              {r.status === "sent" && <Button size="sm" variant="outline" onClick={() => setQ({ supplier_id: "", valid_until: "", lines: r.lines.map((l) => ({ item_id: l.item_id, item_code: l.item_code, unit_price: "", lead_time_days: "", tax_pct: 18, moq: "" })) })} data-testid="add-quote-btn"><Plus className="w-4 h-4 mr-1" /> Record Quotation</Button>}
            </div>
            <div className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Scale className="w-4 h-4" /> Comparison <span className="text-xs font-normal text-slate-500">— recommended = 60% price, 20% lead time, 20% supplier performance</span></div>
            {cmp.items.map((it) => (
              <div key={it.item_id} className="bg-white border border-slate-200 rounded-lg p-3">
                <div className="text-sm font-semibold">{it.item_code} · {it.item_name} <span className="text-slate-500 font-normal">— {fmtNum(it.qty)} {it.uom}</span></div>
                {it.offers.length === 0 ? <div className="text-xs text-slate-500 mt-1">No quotes yet.</div> : (
                  <table className="w-full text-xs mt-2">
                    <thead><tr className="text-left text-slate-500"><th className="py-1">Award</th><th>Supplier</th><th>Quote</th><th className="text-right">Unit Price</th><th className="text-right">Tax %</th><th className="text-right">Lead (d)</th><th className="text-right">MOQ</th><th className="text-right">Perf.</th><th className="text-right">Amount</th><th className="text-right">Score</th><th /></tr></thead>
                    <tbody>
                      {it.offers.map((o) => (
                        <tr key={o.quotation_id} className={`border-t border-slate-100 ${o.recommended ? "bg-emerald-50/60" : ""}`}>
                          <td className="py-1"><input type="radio" disabled={r.status !== "sent"} name={`aw-${it.item_id}`} checked={awards[it.item_id] === o.quotation_id} onChange={() => setAwards({ ...awards, [it.item_id]: o.quotation_id })} /></td>
                          <td>{o.supplier_name}</td><td className="mono">{o.quotation_code}</td>
                          <td className="text-right mono">{fmtMoney(o.unit_price)}</td><td className="text-right">{o.tax_pct}</td>
                          <td className="text-right">{o.lead_time_days}</td><td className="text-right">{fmtNum(o.moq)}</td>
                          <td className="text-right">{o.performance_score ?? "—"}</td><td className="text-right mono">{fmtMoney(o.amount)}</td>
                          <td className="text-right font-semibold">{o.score}</td>
                          <td className="text-[10px] space-x-1">{o.lowest_price && <span className="text-emerald-700">lowest</span>}{o.fastest && <span className="text-blue-700">fastest</span>}{o.recommended && <span className="text-amber-700">recommended</span>}{!o.valid && <span className="text-red-600">expired</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
            {r.status === "sent" && Object.keys(awards).length > 0 && (
              <div className="flex justify-end"><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={award} data-testid="award-btn"><Award className="w-4 h-4 mr-1" /> Award & create Purchase Request</Button></div>
            )}
          </div>
        )}
        {q && (
          <div className="border-t border-slate-200 pt-4 space-y-3" data-testid="quote-form">
            <div className="grid grid-cols-3 gap-3">
              <Field label="Supplier"><Sel value={q.supplier_id} onChange={(v) => setQ({ ...q, supplier_id: v })} options={r.suppliers.map((s) => ({ value: s.id, label: s.name }))} tid="quote-supplier" /></Field>
              <Field label="Supplier Quote Ref"><Input value={q.supplier_ref || ""} onChange={(e) => setQ({ ...q, supplier_ref: e.target.value })} /></Field>
              <Field label="Valid Until"><Input type="date" value={q.valid_until} onChange={(e) => setQ({ ...q, valid_until: e.target.value })} /></Field>
            </div>
            {q.lines.map((l, i) => (
              <div key={l.item_id} className="grid grid-cols-5 gap-2 items-end">
                <div className="text-sm mono">{l.item_code}</div>
                {["unit_price", "tax_pct", "lead_time_days", "moq"].map((key) => (
                  <Field key={key} label={titleCase(key)}><Input type="number" value={l[key]} onChange={(e) => setQ({ ...q, lines: q.lines.map((x, j) => (j === i ? { ...x, [key]: e.target.value } : x)) })} data-testid={`quote-${key}-${i}`} /></Field>
                ))}
              </div>
            ))}
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setQ(null)}>Cancel</Button><Button onClick={addQuote} data-testid="save-quote-btn">Save Quotation</Button></div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ProcurementPlan() {
  const [d, setD] = useState(null);
  const [picked, setPicked] = useState({});
  const load = useCallback(() => api.get("/supplier-master/procurement-plan").then((r) => { setD(r.data); setPicked({}); }).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const makePr = async () => {
    const ids = Object.keys(picked).filter((k) => picked[k]);
    try {
      const { data } = await api.post("/supplier-master/procurement-plan/purchase-request", { item_ids: ids });
      toast.success(`${data.code} created with ${data.lines} line(s)`); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const cols = [
    { key: "_p", label: "", render: (r) => <input type="checkbox" checked={!!picked[r.item_id]} onChange={(e) => setPicked({ ...picked, [r.item_id]: e.target.checked })} /> },
    { key: "item_code", label: "Item" }, { key: "item_name", label: "Description" }, { key: "uom", label: "UOM" },
    { key: "physical_stock", label: "Physical", type: "number" }, { key: "reserved_qty", label: "Reserved", type: "number" },
    { key: "available_qty", label: "Available", type: "number" }, { key: "min_stock_level", label: "Min Stock", type: "number" },
    { key: "shortfall_qty", label: "Order Shortfall", type: "number" }, { key: "on_order_qty", label: "On Order (PR/PO)", type: "number" },
    { key: "uncovered_qty", label: "Net Requirement", type: "number" }, { key: "recommended_order_qty", label: "Order Qty", type: "number" },
    { key: "supplier_name", label: "Supplier", render: (r) => (r.supplier_id ? <Link className="text-blue-600 hover:underline" to={`/supplier-master/${r.supplier_id}`}>{r.supplier_name}</Link> : r.supplier_name || <span className="text-red-600">none</span>) },
    { key: "supplier_role", label: "Basis" }, { key: "unit_price", label: "Price", type: "money" }, { key: "estimated_cost", label: "Est. Cost", type: "money" },
    { key: "lead_time_days", label: "Lead (d)", type: "number" }, { key: "expected_date", label: "Expected", type: "date" },
    { key: "backup_suppliers", label: "Backups", render: (r) => (r.backup_suppliers || []).map((b) => `${b.supplier_name} (${b.role})`).join(", ") || "—" },
  ];
  return (
    <ModuleShell kind="supplier" title="Procurement Plan" tid="procurement-plan"
      subtitle="Available = Physical − Reserved. Requirement = (Min Stock − Available) + shortfall against open Sales Order − already on order; rounded to the chosen supplier's MOQ/packaging, supplier picked by preference, performance, price and lead time."
      actions={d?.rows?.length ? (
        <>
          <ExportButtons kind="supplier" title="Procurement Plan" columns={cols.filter((c) => !c.key.startsWith("_") && c.key !== "backup_suppliers")} rows={d.rows} />
          <Button className="bg-blue-600 hover:bg-blue-700" size="sm" onClick={makePr} data-testid="plan-pr-btn">
            <ShoppingCart className="w-4 h-4 mr-1" /> Create PR {Object.values(picked).some(Boolean) ? "(selected)" : "(all)"}
          </Button>
        </>
      ) : null}>
      {!d ? <div className="text-sm text-slate-500">Loading…</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Items to Procure" value={d.summary.items ?? 0} />
            <Kpi label="Estimated Cost" value={fmtMoney(d.summary.estimated_cost)} />
            <Kpi label="Suppliers" value={d.summary.suppliers ?? 0} />
            <Kpi label="Without Supplier" value={d.summary.without_supplier ?? 0} tone={d.summary.without_supplier ? "text-red-600" : "text-slate-900"} />
          </div>
          <DataTable rows={d.rows} columns={cols} tid="plan-table" empty="Nothing to procure — every item is covered by stock or open PRs/POs." />
        </>
      )}
    </ModuleShell>
  );
}
